import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PERF_FILE_LIMITS, formatFileSize, perfFileNameProblem, type PerfFileView } from '@1qa/shared';
import { Check, Copy, FileText, Image, Trash2, Upload } from '@/components/icons';
import { api, errorMessage, fetchPerfFileBlob } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { copyText, timeAgo } from '@/lib/format';
import { perfFilesKey, usePerfFiles } from '@/lib/perfFiles';
import { Badge, Button, Card, CardBody, CardHeader, useConfirm, useToast } from '@/components/ui';

const isPreviewable = (type: string) => /^image\/(png|jpe?g|gif|webp|avif|bmp)$/i.test(type);

/** A small preview of an image test file (fetched with the sign-in header), else a file icon. */
export function FileThumb({ file, className = 'size-10' }: { file: Pick<PerfFileView, 'id' | 'name' | 'contentType' | 'sha256'>; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const previewable = isPreviewable(file.contentType);
  useEffect(() => {
    if (!previewable) return;
    let gone = false;
    let made: string | null = null;
    fetchPerfFileBlob(file.id)
      .then((blob) => {
        if (gone) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      })
      .catch(() => undefined);
    return () => {
      gone = true;
      if (made) URL.revokeObjectURL(made);
    };
    // A replaced file has a new checksum: load the new picture.
  }, [file.id, file.sha256, previewable]);
  return (
    <span className={`grid shrink-0 place-items-center overflow-hidden rounded-lg bg-surface-sunken text-fg-muted ring-1 ring-border-subtle ring-inset ${className}`}>
      {url ? <img src={url} alt="" className="size-full object-cover" /> : previewable ? <Image className="size-4" aria-hidden /> : <FileText className="size-4" aria-hidden />}
    </span>
  );
}

function CopyName({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`Copy the name ${name}`}
      title="Copy the name (for a data file column)"
      onClick={async () => {
        if (await copyText(name)) {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }
      }}
      icon={copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
    />
  );
}

/**
 * The project's test files (Scripts tab): images and other files scripts upload, with a preview, size and the scripts
 * that send them. Upload several at once (each keeps its file name), replace one under the same name, delete unused ones.
 */
export function TestFilesCard({ projectId, orgId }: { projectId: string; orgId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { can, canModify } = useAuth();
  const files = usePerfFiles(projectId);
  const picker = useRef<HTMLInputElement>(null);
  const replacer = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<PerfFileView | null>(null);
  const [progress, setProgress] = useState<{ name: string; fraction: number } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: perfFilesKey(projectId) });
  const list = files.data ?? [];
  const totalBytes = list.reduce((n, f) => n + f.sizeBytes, 0);

  const upload = useMutation({
    mutationFn: async (chosen: File[]) => {
      const saved: string[] = [];
      const failed: string[] = [];
      for (const file of chosen) {
        const problem = file.size > PERF_FILE_LIMITS.bytes ? `larger than ${formatFileSize(PERF_FILE_LIMITS.bytes)}` : perfFileNameProblem(file.name);
        if (problem) {
          failed.push(`${file.name}: ${problem}`);
          continue;
        }
        try {
          setProgress({ name: file.name, fraction: 0 });
          await api.uploadPerfFile(projectId, file, file.name, (fraction) => setProgress({ name: file.name, fraction }));
          saved.push(file.name);
        } catch (e) {
          failed.push(`${file.name}: ${errorMessage(e)}`);
        }
      }
      return { saved, failed };
    },
    onSuccess: ({ saved, failed }) => {
      if (saved.length) toast.success(saved.length === 1 ? `${saved[0]} uploaded` : `${saved.length} files uploaded`);
      for (const f of failed) toast.error(f);
    },
    onSettled: () => (setProgress(null), refresh()),
  });
  const replace = useMutation({
    mutationFn: ({ target, file }: { target: PerfFileView; file: File }) => api.replacePerfFile(target.id, file, (fraction) => setProgress({ name: target.name, fraction })),
    onSuccess: (f) => toast.success(`${f.name} replaced: scripts send the new file from the next run`),
    onError: (e) => toast.error(errorMessage(e)),
    onSettled: () => (setProgress(null), setReplacing(null), refresh()),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deletePerfFile(id),
    onSuccess: () => (toast.success('Test file deleted'), refresh()),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const busy = upload.isPending || replace.isPending;

  return (
    <Card>
      <CardHeader
        title="Test files"
        description={`Images and other files your scripts upload, up to ${formatFileSize(PERF_FILE_LIMITS.bytes)} each. In the script editor, choose one for an upload field, or write {{IMAGE}} and list a file name per row in a data file, so each journey uploads a different one.`}
        actions={
          can('test.write', orgId) && (
            <Button variant="outline" size="sm" onClick={() => picker.current?.click()} loading={upload.isPending} icon={<Upload className="size-4" />}>
              Upload files
            </Button>
          )
        }
      />
      <input
        ref={picker}
        type="file"
        multiple
        className="sr-only"
        aria-label="Choose test files"
        onChange={(e) => {
          const chosen = [...(e.target.files ?? [])];
          e.target.value = '';
          if (chosen.length) upload.mutate(chosen);
        }}
      />
      <input
        ref={replacer}
        type="file"
        className="sr-only"
        aria-label="Choose the new file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && replacing) replace.mutate({ target: replacing, file });
        }}
      />
      <CardBody className="space-y-3 text-sm">
        {progress && (
          <div role="status" className="space-y-1.5">
            <p className="text-xs text-fg-secondary">
              Uploading {progress.name}… {Math.round(progress.fraction * 100)}%
            </p>
            <div className="h-1 overflow-hidden rounded-full bg-surface-raised">
              <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
            </div>
          </div>
        )}
        {list.length === 0 ? (
          <p className="text-fg-muted">No test files yet.</p>
        ) : (
          <>
            <ul className="divide-y divide-border-subtle" data-testid="perf-test-files">
              {list.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <FileThumb file={f} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-xs text-fg">{f.name}</div>
                    <div className="truncate text-xs text-fg-muted">
                      {formatFileSize(f.sizeBytes)} · {f.contentType} · {timeAgo(f.createdAt)}
                      {f.createdBy ? ` by ${f.createdBy.name}` : ''}
                    </div>
                  </div>
                  {f.usedBy.length > 0 ? <Badge tone="neutral">Sent by {f.usedBy.length === 1 ? f.usedBy[0]!.name : `${f.usedBy.length} scripts`}</Badge> : null}
                  <CopyName name={f.name} />
                  {f.createdBy && canModify('test.write', f.createdBy.id, orgId) && (
                    <span className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setReplacing(f);
                          replacer.current?.click();
                        }}
                        icon={<Upload className="size-3.5" />}
                      >
                        Replace
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${f.name}`}
                        disabled={f.usedBy.length > 0}
                        title={f.usedBy.length ? 'A script sends it' : undefined}
                        onClick={async () => {
                          if (await confirm({ title: 'Delete this test file?', message: `“${f.name}” will be deleted. Data files that name it make their journeys fail until it is uploaded again.`, confirmLabel: 'Delete' })) remove.mutate(f.id);
                        }}
                        icon={<Trash2 className="size-3.5" />}
                      />
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-fg-muted">
              {list.length} of {PERF_FILE_LIMITS.files} files · {formatFileSize(totalBytes)} of {formatFileSize(PERF_FILE_LIMITS.totalBytes)}
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}
